/**
 * Type-safe API client for Edge Functions
 *
 * Security rules:
 * - Token from supabase.auth.getSession() only
 * - Never pass tenant_id in request body (derived from JWT)
 * - Never request encrypted fields
 *
 * Features:
 * - Automatic 401 handling with session refresh attempt
 * - Request timeout (30 seconds)
 * - Session expiry detection
 */

import { supabase } from '@/lib/supabase';
import { sessionEvents } from '@/lib/session-events';
import { ApiClientError, type ApiErrorResponse } from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error('Missing VITE_API_BASE_URL environment variable');
}

/**
 * Default request timeout in milliseconds
 */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Maximum retry attempts after 401 (prevents infinite refresh loops)
 */
const MAX_401_RETRIES = 1;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | string[] | number | undefined>;
  /**
   * Custom timeout in milliseconds (default: 30000)
   */
  timeout?: number;
  /**
   * Internal: tracks retry attempts after 401
   */
  _retryCount?: number;
}

/**
 * Flag to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Attempt to refresh the session
 * Returns true if refresh succeeded, false otherwise
 */
async function attemptSessionRefresh(): Promise<boolean> {
  // If already refreshing, wait for that attempt
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) {
        console.warn('[API Client] Session refresh failed:', error?.message);
        return false;
      }
      console.info('[API Client] Session refreshed successfully');
      return true;
    } catch (error) {
      console.error('[API Client] Session refresh error:', error);
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Handle 401 response: attempt refresh, emit event if failed
 */
async function handle401(): Promise<boolean> {
  const refreshed = await attemptSessionRefresh();

  if (!refreshed) {
    // Emit event for AuthProvider to handle sign out
    sessionEvents.emit('session:refresh_failed');
    return false;
  }

  return true;
}

/**
 * Make an authenticated request to an Edge Function
 */
export async function apiClient<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, params, timeout = REQUEST_TIMEOUT_MS } = options;

  // Get current session token
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    // No session at all - emit event and throw
    sessionEvents.emit('session:expired');
    throw new ApiClientError('UNAUTHORIZED', 'No active session', 'client', 401);
  }

  // Build URL with query params
  const url = new URL(`${API_BASE_URL}/${endpoint}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          url.searchParams.set(key, value.join(','));
        } else {
          url.searchParams.set(key, String(value));
        }
      }
    });
  }

  const headers: HeadersInit = {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };

  const fetchOptions: RequestInit = {
    method,
    headers,
  };
  if (body) {
    fetchOptions.body = JSON.stringify(body);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  fetchOptions.signal = controller.signal;

  let response: Response;
  try {
    response = await fetch(url.toString(), fetchOptions);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiClientError(
        'TIMEOUT',
        `Request timed out after ${timeout}ms`,
        'client',
        0
      );
    }
    throw new ApiClientError(
      'NETWORK_ERROR',
      error instanceof Error ? error.message : 'Network request failed',
      'client',
      0
    );
  } finally {
    clearTimeout(timeoutId);
  }

  // Handle 401 with refresh attempt (with retry limit to prevent infinite loops)
  if (response.status === 401) {
    const retryCount = options._retryCount ?? 0;

    if (retryCount >= MAX_401_RETRIES) {
      // Already retried after refresh - backend keeps rejecting, sign out
      console.warn('[API Client] 401 persists after token refresh, signing out');
      sessionEvents.emit('session:refresh_failed');
      throw new ApiClientError(
        'SESSION_EXPIRED',
        'Your session has expired. Please sign in again.',
        'client',
        401
      );
    }

    const refreshed = await handle401();
    if (refreshed) {
      // Retry the request with new token (increment retry count)
      return apiClient<T>(endpoint, { ...options, _retryCount: retryCount + 1 });
    }
    // Refresh failed - throw with clear message
    throw new ApiClientError(
      'SESSION_EXPIRED',
      'Your session has expired. Please sign in again.',
      'client',
      401
    );
  }

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    throw new ApiClientError(
      'INVALID_RESPONSE',
      'Invalid response format',
      'client',
      response.status
    );
  }

  const data = await response.json();

  if (!response.ok) {
    // Handle various error response formats
    const apiError = data as ApiErrorResponse;
    if (apiError?.error?.code) {
      throw new ApiClientError(
        apiError.error.code,
        apiError.error.message || 'Unknown error',
        apiError.error.request_id || 'unknown',
        response.status
      );
    }
    // Fallback for unexpected error formats
    throw new ApiClientError(
      'API_ERROR',
      typeof data === 'object' && data !== null && 'message' in data
        ? String(data.message)
        : `Request failed with status ${response.status}`,
      'unknown',
      response.status
    );
  }

  return data as T;
}
